import React, { Suspense, useState, FC, useContext } from 'react';
import {
  Box,
  Text,
  Separator,
  SeparatorType,
  SideSheetFooter,
  ToastAction,
  ToastType,
  useToastsDispatch,
  Spinner,
} from '@bechtel/gds';
import { Plural, t, Trans } from '@lingui/macro';
import { RodRoomAttendantSelector } from '~/components/molecules';
import {
  CrewMember,
  IssueCaddiesInput,
  IssueCaddiesPayload,
  IssueCaddyInput,
  PersonaType,
  RodRequestCaddy,
} from '@bechtel/types';
import { CaddyContainerStyled, CaddyCardsContainerStyled } from './styled';
import { findIndex, flatMap, get, map, noop, uniqBy } from 'lodash';
import { CaddyTabType } from '../types';
import { AddCaddySectionStyled } from './styled';
import { ErrorBoundary, SuspenseLoader, FooterButtonStyled, useAppUser } from '@bechtel/client-common';
import { DeleteCaddy } from '../delete';
import { EditCaddy } from '../edit';
import { CaddyList } from '../list';
import { AddCaddyDetail } from './detail';
import { useMutation } from '@apollo/client';
import { ISSUE_CADDIES } from '@bechtel/client-gql';
import { IssueCaddiesRequestError } from './error/types';
import IssueCaddiesErrorModal from './error';
import { useRodRoomPermissions } from '@bechtel/client-weld-common';
import { RodRoomDataContext } from '~/utilities/context';

interface AddCaddyProps {
  setSelectedTab: (tabName: CaddyTabType) => void;
  welders: CrewMember[];
  rodRequestId: string | undefined;
  updateCaddyCount: (count: number) => void;
}

export const AddCaddy: FC<AddCaddyProps> = ({ setSelectedTab, welders, rodRequestId, updateCaddyCount }) => {
  const [caddies, setCaddies] = useState<RodRequestCaddy[]>([]);
  const [editCaddy, setEditCaddy] = useState<RodRequestCaddy | undefined>(undefined);
  const [deleteCaddy, setDeleteCaddy] = useState<RodRequestCaddy | undefined>(undefined);
  const [signedBy, setSignedBy] = useState<CrewMember | undefined>(undefined);
  const { selectedRodRoom } = useContext(RodRoomDataContext);
  const dispatchToast = useToastsDispatch();
  const [issueCaddiesError, setIssueCaddiesError] = useState<IssueCaddiesRequestError | null>();
  const { user } = useAppUser();
  const { canIssueCaddies } = useRodRoomPermissions();
  const onAddCaddy = (caddy: RodRequestCaddy) => {
    const caddyIndex = findIndex(caddies, (item) => {
      return item.caddy.id === caddy.caddy.id;
    });
    if (caddyIndex === -1) {
      setCaddies((prev) => {
        updateCaddyCount(prev.length + 1);
        return [...caddies, caddy];
      });
    }
  };

  const AddCaddyToastContent = () => {
    const uniqueWelders: CrewMember[] = uniqBy(
      flatMap(caddies, (caddy) => caddy.stencils),
      'id'
    );
    return (
      <Box pb={{ sm: '550' }} mb={{ sm: '400' }} css={{ borderBottom: '1px solid rgba(255, 255, 255, 0.22)' }}>
        <Text family="graphik" weight="400" size="500">
          <Plural
            value={caddies.length}
            one="1 Caddy has been issued against"
            other={`${caddies.length} Caddies have been issued against`}
          />{' '}
          <Plural value={uniqueWelders.length} one="1 Welder" other={`${uniqueWelders.length} Welders`} />
        </Text>
      </Box>
    );
  };

  const openAddCaddyToast = () => {
    const createAddCaddyToast: ToastAction = {
      type: 'ADD_TOAST',
      payload: {
        toastType: ToastType.INFO,
        content: AddCaddyToastContent,
        autoDismissTimeout: 7000,
        alertAction: {
          label: t`Got It`,
          onClickHandler: () => noop,
        },
      },
    };
    dispatchToast(createAddCaddyToast);
    setSelectedTab(CaddyTabType.ISSUED);
  };

  const [onIssueCaddies, { loading }] = useMutation<
    { issueCaddies: IssueCaddiesPayload },
    { input: IssueCaddiesInput }
  >(ISSUE_CADDIES, {
    onCompleted: (data) => {
      const issueCaddiesError = get(data, 'issueCaddies.error');
      if (issueCaddiesError) {
        setIssueCaddiesError(issueCaddiesError);
      } else {
        openAddCaddyToast();
      }
    },
    refetchQueries: ['caddies'],
  });

  const onDeleteCaddy = () => {
    setCaddies((prev) => {
      const caddyIndex = findIndex(prev, (item) => {
        return item.caddy.id === deleteCaddy?.caddy.id;
      });
      const previousCaddies = [...prev];
      if (caddyIndex !== -1) previousCaddies.splice(caddyIndex, 1);
      updateCaddyCount(prev.length - 1);
      return previousCaddies;
    });
    setDeleteCaddy(undefined);
  };

  const issueCaddies = () => {
    const issueCaddiesInput: IssueCaddyInput[] = map(caddies, (caddy) => {
      return {
        pickedUpBy: caddy?.pickedUpBy?.id ? caddy.pickedUpBy.id : '',
        caddyId: caddy?.caddy.id ? caddy.caddy.id : '',
        fillerMaterialId: caddy?.fillerMaterial?.fillerMaterial.id ? caddy.fillerMaterial.fillerMaterial.id : '',
        issuedToStencilIds: caddy?.stencils?.length ? map(caddy.stencils, (item) => item.id) : [],
        issuedBy: signedBy?.id ? signedBy.id : '',
        fillerMaterialDiameterDetailId: map(caddy.fillerMaterial.diameters, (diameter) => diameter.heatLotNumber.id),
      };
    });
    onIssueCaddies({
      variables: {
        input: {
          rodRequestId,
          caddies: issueCaddiesInput,
        },
      },
    });
  };

  const closeModal = () => {
    setIssueCaddiesError(null);
  };

  return (
    <>
      <AddCaddySectionStyled
        data-testid="add-caddy-container"
        isRodRoomAttendant={user?.crewMember?.persona === PersonaType.ROD_ROOM_ATTENDANT}
      >
        <Box flexDirection={{ sm: 'column' }} style={{ height: '100%' }}>
          <Box mx={{ sm: '400' }} mt={{ sm: '400' }} mb={{ sm: '200' }}>
            <CaddyContainerStyled>
              <AddCaddyDetail
                welders={welders}
                onAddCaddy={(caddy: RodRequestCaddy) => {
                  onAddCaddy(caddy);
                }}
                rodRoomId={selectedRodRoom?.id}
                selectedCaddies={map(caddies, (caddy) => caddy.caddy)}
              />
            </CaddyContainerStyled>
          </Box>
          {caddies.length > 0 && (
            <CaddyCardsContainerStyled>
              <Text family="graphik" size="400" color="grey.1000" weight="600">
                {caddies.length}&nbsp;<Trans>Caddy ready for issuing</Trans>
              </Text>
              <Separator type={SeparatorType.HORIZONTAL} spacing="500" color="grey.300" />
              <CaddyList
                canEditCaddy={true}
                caddies={caddies}
                onEditCaddy={(caddy: RodRequestCaddy) => {
                  setEditCaddy(caddy);
                }}
                onDeleteCaddy={(caddy: RodRequestCaddy) => {
                  setDeleteCaddy(caddy);
                }}
              />
            </CaddyCardsContainerStyled>
          )}
        </Box>
        {deleteCaddy && (
          <ErrorBoundary>
            <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
              <DeleteCaddy
                caddyNumber={deleteCaddy.caddy.id}
                onCancel={() => {
                  setDeleteCaddy(undefined);
                }}
                onDelete={onDeleteCaddy}
              />
            </Suspense>
          </ErrorBoundary>
        )}
        {editCaddy && (
          <ErrorBoundary>
            <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
              <EditCaddy
                caddy={editCaddy}
                onClose={() => {
                  setEditCaddy(undefined);
                }}
                onUpdateCaddy={(caddy: RodRequestCaddy) => {
                  setCaddies((prev) => {
                    const caddyIndex = findIndex(prev, (item) => {
                      return item.caddy.id === caddy.caddy.id;
                    });
                    const prevCaddies = [...prev];
                    prevCaddies.splice(caddyIndex, 1, caddy);
                    return prevCaddies;
                  });
                  setEditCaddy(undefined);
                }}
                rodRoomId={selectedRodRoom?.id}
                welders={welders}
              />
            </Suspense>
          </ErrorBoundary>
        )}
      </AddCaddySectionStyled>
      <SideSheetFooter>
        <Box ml={{ sm: '500' }} justifyContent={{ sm: 'space-between' }}>
          <Box alignItems={{ sm: 'center' }}>
            <Text color="white" size="300">
              <Trans>Signed By:</Trans>
            </Text>
            <Box ml={{ sm: '300' }} style={{ width: '250px' }}>
              <RodRoomAttendantSelector
                selectedItem={signedBy}
                onRodRoomAttendantSelection={(selectedItem) => {
                  setSignedBy(selectedItem);
                }}
              />
            </Box>
          </Box>
          {canIssueCaddies && (
            <FooterButtonStyled
              disabled={signedBy === undefined || caddies.length === 0 || loading}
              onClick={issueCaddies}
              data-testid="issue-caddies-btn"
            >
              <Trans>Issue</Trans>
              {loading && (
                <>
                  &nbsp; <Spinner />
                </>
              )}
            </FooterButtonStyled>
          )}
        </Box>
      </SideSheetFooter>
      {issueCaddiesError && (
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          <IssueCaddiesErrorModal issueCaddiesError={issueCaddiesError} close={closeModal} />
        </Suspense>
      )}
    </>
  );
};
